import { FunctionComponent } from "react";

import { faEnvelope } from "@fortawesome/pro-regular-svg-icons";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";

import { useTranslation } from "react-i18next";

import { Icon } from "@/components";
import { BrandsIcon } from "@/components/BrandsIcon/BrandsIcon";
import { classNames } from "@/utils/classNames";

/** "Robert Vossen" → "RV" (first letter of the first two words). */
export const senderInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? "")
		.join("");

/**
 * Avatar initials circle with a small channel badge (mail / LinkedIn) in its
 * corner — the identity visual shared by the from-picker trigger token and
 * the picker's option rows (mockup `.ft-ava` / `.fo-ava`).
 */
export const SenderIdentityAvatar: FunctionComponent<{
	name: string;
	channel: "email" | "linkedin";
	className?: string;
}> = ({ name, channel, className }) => (
	<span
		className={classNames(
			"relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-action/15 text-[9px] font-bold text-blue-action",
			className ?? ""
		)}
	>
		{senderInitials(name)}
		<span className="absolute -bottom-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-border-light bg-white">
			{channel === "linkedin" ? (
				<BrandsIcon className="h-2 w-2 text-brand-linkedin" icon={faLinkedinIn as IconDefinition} />
			) : (
				<Icon className="h-2 w-2 text-blue-action" icon={faEnvelope} />
			)}
		</span>
	</span>
);

/** Primary / Secondary email-alias chip (from the mailbox's `type` column). */
export const IdentityTagChip: FunctionComponent<{ tag: "primary" | "secondary" }> = ({ tag }) => {
	const { t } = useTranslation();

	return (
		<span
			className={classNames(
				"rounded px-1 py-px text-[9px] font-semibold uppercase tracking-wide",
				tag === "primary" ? "bg-blue-action/10 text-blue-action" : "bg-gray-100 text-text-meta"
			)}
		>
			{t(`inbox.identity.${tag}`)}
		</span>
	);
};
